import { useCallback, useRef, useState } from 'react'

import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { CloseCircle } from 'entities/close-circle/model'
import userStore from 'entities/user/module/user-store'

import { CloseCircleApi } from 'shared/api/close-circle'
import { getLocalStore } from 'shared/lib/local-store'
import AppLoaderGlobal from 'shared/ui/app-loader-global'

import { INSURANCE_INTRO_SETTING_KEY } from '../model/constants'
import { InsuranceDraftProvider } from '../model/insurance-draft'
import { InsuranceCitizenship, InsuranceTripPurpose } from '../model/types'
import ApplicationInsurancePage from '../pages/application'
import CalculationInsurancePage from '../pages/calculation'
import CalendarInsurancePage from '../pages/calendar'
import CitizenshipInsurancePage from '../pages/citizenship'
import CountryInsurancePage from '../pages/country'
import IntroInsurancePage from '../pages/intro'
import PolicyholderInsurancePage from '../pages/policyholder'
import ProgramInsurancePage from '../pages/program'
import ScanPassportInsurancePage from '../pages/scan-passport'

const InsuranceIndexPage = observer(() => {
  const [searchParams] = useSearchParams()
  const deviceId =
    searchParams.get('device-id') || getLocalStore('mTravelDeviceId')

  if (deviceId && userStore.userSettings === undefined) {
    return <AppLoaderGlobal />
  }

  if (userStore.getClientStateString(INSURANCE_INTRO_SETTING_KEY)) {
    return (
      <Navigate
        to={RoutingPaths.insurance.application.path()}
        replace
      />
    )
  }

  return <IntroInsurancePage />
})

export const InsurancePages = () => {
  const [purposes, setPurposes] = useState<InsuranceTripPurpose[] | null>(null)
  const [citizenships, setCitizenships] = useState<
    InsuranceCitizenship[] | null
  >(null)
  const [closeCircles, setCloseCircles] = useState<CloseCircle[] | null>(null)
  const closeCirclesRequest = useRef<Promise<CloseCircle[] | null> | null>(null)

  const loadCloseCircles = useCallback(() => {
    if (closeCircles !== null) return Promise.resolve(closeCircles)
    if (closeCirclesRequest.current) return closeCirclesRequest.current

    closeCirclesRequest.current = CloseCircleApi.getUserDocuments()
      .then(({ data }) => {
        setCloseCircles(data.data)
        return data.data
      })
      .catch(() => null)
      .finally(() => {
        closeCirclesRequest.current = null
      })

    return closeCirclesRequest.current
  }, [closeCircles])

  return (
    <InsuranceDraftProvider>
      <Routes>
        <Route
          index
          element={<InsuranceIndexPage />}
        />
        <Route
          path={RoutingPaths.insurance.application.base()}
          element={
            <ApplicationInsurancePage
              purposes={purposes}
              onPurposesLoaded={setPurposes}
            />
          }
        />
        <Route
          path={RoutingPaths.insurance.calendar.base()}
          element={<CalendarInsurancePage />}
        />
        <Route
          path={RoutingPaths.insurance.country.base()}
          element={<CountryInsurancePage />}
        />
        <Route
          path={RoutingPaths.insurance.program.base()}
          element={<ProgramInsurancePage />}
        />
        <Route
          path={RoutingPaths.insurance.policyholder.base()}
          element={
            <PolicyholderInsurancePage
              citizenships={citizenships}
              closeCircles={closeCircles}
              onCitizenshipsLoaded={setCitizenships}
              loadCloseCircles={loadCloseCircles}
            />
          }
        />
        <Route
          path={RoutingPaths.insurance.citizenship.base()}
          element={
            <CitizenshipInsurancePage
              citizenships={citizenships}
              onCitizenshipsLoaded={setCitizenships}
            />
          }
        />
        <Route
          path={RoutingPaths.insurance.scanPassport.base()}
          element={<ScanPassportInsurancePage />}
        />
        <Route
          path={RoutingPaths.insurance.calculation.base()}
          element={<CalculationInsurancePage />}
        />
        <Route
          path="*"
          element={
            <Navigate
              to={RoutingPaths.insurance.path()}
              replace
            />
          }
        />
      </Routes>
    </InsuranceDraftProvider>
  )
}
